import EventEmitter from "event-lite";
import { subscribeKey } from "valtio/utils";

import { readState } from "@/__main__/app-state.mjs";
import { IS_APP } from "@/__main__/constants.mjs";
import mainRefs from "@/__main__/refs.mjs";
import router, { EVENT_CHANGE_ROUTE } from "@/__main__/router.mjs";
import { GAME_SHORT_NAMES } from "@/app/constants.mjs";
import { TARGETING_PLATFORM } from "@/feature-ads/constants/constants.mjs";
import { setAditudePageTargeting } from "@/feature-ads/display-vendor-aditude.mjs";
import { getAgeGroup } from "@/feature-ads/util.mjs";
import { devError } from "@/util/dev.mjs";
import { findGameSymbol } from "@/util/game-route.mjs";
import getOSType from "@/util/get-os-type.mjs";
import { stringifyPath } from "@/util/path-regexp.mjs";
import SymbolMap from "@/util/symbol-map.mjs";
import symbolName from "@/util/symbol-name.mjs";

const GAME_NAMES_LIST = SymbolMap.values(GAME_SHORT_NAMES);

// select up to the first non-game word in a route
export function simplifyPath(
  path: string | RegExp,
  fallback = null,
  str = stringifyPath(path),
) {
  return (
    str
      // select up to the first symbol, starting after initial slash
      ?.match(/^\/([\w-][/\w-]+[\w-])/)?.[1]
      // now we want the first non-game word
      ?.split("/")
      .find(
        (w: (typeof GAME_NAMES_LIST)[number]) => !GAME_NAMES_LIST.includes(w),
      ) || fallback
  );
}

export const displayEvents = Object.assign(new EventEmitter(), {
  SET_TARGETING: symbolName("display-set-targeting"),
  ready: initEvents,
});

export const setTargeting = (
  key: string,
  value: string | { toString: () => string },
  slot?: unknown,
) => {
  displayEvents.emit(displayEvents.SET_TARGETING, key, value, slot);
};

function initEvents(displayProvider) {
  setTargeting("platform", TARGETING_PLATFORM);
  setTargeting("version", mainRefs.PKG_VERSION);
  setTargeting("prod", IS_APP ? "1" : "0");
  setTargeting("display_provider", displayProvider);

  setTargeting("mt", getOSType().charCodeAt(0));

  const setUserTargeting = () => {
    const gender = readState.user?.gender;
    setTargeting("gender", gender?.toLowerCase?.() || "unknown");

    const ageGroup = getAgeGroup(readState.user?.birthday);
    setTargeting("age_group", ageGroup || "unknown");
  };
  subscribeKey(readState, "user", setUserTargeting);
  const prevKeys = Object.assign([], {
    clear() {
      for (const key of this) setTargeting(key, null);
      this.length = 0;
    },
  });
  // apply custom fields based on regex route
  const applyRegexTargeting = (route) => {
    // clear previous custom fields
    prevKeys.clear();

    const { path, parameters } = route;
    if (!(typeof path === "object" && path?.originalPath)) return;
    const keys = path.keys;
    if (!keys) return;
    for (const i in keys) {
      const descriptor = keys[i];
      const value = parameters[i];
      const { name } = descriptor;
      if (!(name && value)) continue;
      const gptKey = `route-${name}`;
      prevKeys.push(gptKey);
      if (value) {
        setTargeting(gptKey, value);
      } else {
        devError(`Missing targeting value for "${name}"`);
      }
    }
  };
  const setPageTargeting = (route = router.route) => {
    setTargeting("left-rail", readState.volatile["ads-left-rail"] ? "1" : "0");
    if (!route) return;
    const { path } = route;
    const gameSymbol = findGameSymbol();
    const shortName = GAME_SHORT_NAMES[gameSymbol];
    setTargeting("game", shortName ?? "n/a");
    applyRegexTargeting(route);

    setTargeting("route", simplifyPath(path));
    setAditudePageTargeting(shortName ?? "n/a", simplifyPath(path));
  };
  router.events.on(EVENT_CHANGE_ROUTE, setPageTargeting);
  setUserTargeting();
  setPageTargeting();
}
